import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, LogOut, ArrowUpRight, LayoutDashboard, Loader2, FolderOpen, Send, Share2, MessageSquare, ChevronDown } from 'lucide-react';
import { Thread } from '../configurator/Thread';
import { ShareDialog } from '../configurator/ShareDialog';
import { toast } from 'sonner';
import { useAuth, logoutUser } from '../hooks/useAuth';
import { computeLayout } from '../configurator/layout';
import { makeCtx } from '../configurator/parts';
import { PerspectiveView, perspVB } from '../configurator/views';
import { getCategory, normalizeConfig } from '../configurator/catalog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = (path, opts = {}) => fetch(`${API_URL}/api${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
const fmtDate = (s) => new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
const STATUS_STYLE = { nouveau: 'text-neon border-neon/40', 'en étude': 'text-gold border-gold/50', 'devis envoyé': 'text-sky-300 border-sky-300/40', archivé: 'text-gray-500 border-white/15' };

function Preview({ config: raw, id }) {
  const config = useMemo(() => normalizeConfig(raw), [raw]);
  const L = useMemo(() => computeLayout(config), [config]);
  const p = `mp${id.slice(0, 6)}`;
  const c = useMemo(() => makeCtx(p, config.mats, config.led, true), [p, config]);
  return <PerspectiveView L={L} c={c} p={p} sel={null} onPick={() => {}} technical vb={perspVB(L, 0.35)} view={0.35} />;
}

function ProjectCard({ pr, onRename, onDelete, onShare, i }) {
  return (
    <article style={{ animationDelay: `${i * 70}ms` }} className="group bg-[#171717] border border-white/10 hover:border-gold/50 transition-colors duration-300 animate-in fade-in slide-in-from-bottom-3 fill-mode-both" data-testid={`project-card-${pr.id}`}>
      <a href={`/configurateur?cat=${pr.category}&project=${pr.id}`} className="block aspect-[4/3] bg-[#f6f1e8] overflow-hidden" data-testid={`project-open-${pr.id}`}>
        <div className="w-full h-full transition-transform duration-700 group-hover:scale-[1.03]"><Preview config={pr.config} id={pr.id} /></div>
      </a>
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{getCategory(pr.category).label}</p>
          <h3 className="font-playfair text-lg text-white truncate" data-testid={`project-name-${pr.id}`}>{pr.name}</h3>
          <p className="text-xs text-gray-500">Modifié le {fmtDate(pr.updated_at)}</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <button type="button" onClick={() => onShare(pr)} title="Partager" data-testid={`project-share-${pr.id}`} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-gold transition-colors"><Share2 className="w-4 h-4" /></button>
          <button type="button" onClick={() => onRename(pr)} title="Renommer" data-testid={`project-rename-${pr.id}`} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
          <button type="button" onClick={() => onDelete(pr)} title="Supprimer" data-testid={`project-delete-${pr.id}`} className="w-9 h-9 flex items-center justify-center text-gray-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    </article>
  );
}

function RequestRow({ r }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(r.unread_client);
  const [count, setCount] = useState(r.messages?.length || 0);
  return (
    <li data-testid={`sent-request-${r.id}`}>
      <div className="flex flex-wrap items-center gap-4 px-5 py-4">
        <Send className="w-4 h-4 text-gold shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <p className="text-white text-sm">{r.category_label} <span className="text-gray-500">· {r.reference}</span></p>
          <p className="text-xs text-gray-500">Envoyée le {fmtDate(r.timestamp)}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1 ${STATUS_STYLE[r.status] || STATUS_STYLE.nouveau}`}>{r.status}</span>
        <button type="button" onClick={() => setOpen(!open)} data-testid={`request-thread-toggle-${r.id}`} className="relative flex items-center gap-2 text-xs text-gray-300 hover:text-gold transition-colors">
          <MessageSquare className="w-4 h-4" />Discussion ({count})
          {unread && <span className="absolute -top-1 -left-1 w-2.5 h-2.5 rounded-full bg-neon animate-pulse" data-testid={`request-unread-${r.id}`} />}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {open && <div className="px-5 pb-5"><Thread path={`/me/configurations/${r.id}/messages`} me="client" onRead={() => setUnread(false)} onCount={setCount} /></div>}
    </li>
  );
}

function Requests({ items }) {
  if (!items.length) return <p className="text-gray-500 text-sm border border-dashed border-white/10 p-8 text-center">Aucune demande envoyée pour le moment. Depuis le configurateur, cliquez sur « Envoyer à JENMA ».</p>;
  return (
    <ul className="divide-y divide-white/10 border border-white/10" data-testid="sent-requests-list">
      {items.map((r) => <RequestRow key={r.id} r={r} />)}
    </ul>
  );
}

function ProjectDialog({ action, onClose, onRename, onDelete }) {
  const [name, setName] = useState('');
  useEffect(() => { if (action) setName(action.pr.name); }, [action]);
  if (!action) return null;
  const del = action.type === 'delete';
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#151515] border-[#a68966]/30 text-gray-200 max-w-md" data-testid={del ? 'delete-project-dialog' : 'rename-project-dialog'}>
        <DialogHeader>
          <DialogTitle className="font-playfair text-2xl text-white">{del ? 'Supprimer ce projet ?' : 'Renommer le projet'}</DialogTitle>
          <DialogDescription className="text-gray-400">{del ? `« ${action.pr.name} » sera définitivement supprimé de votre espace.` : 'Donnez-lui un nom qui vous parle.'}</DialogDescription>
        </DialogHeader>
        {!del && <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="rename-project-input" className="bg-[#0f0f0f] border-white/10 rounded-none" />}
        <div className="flex gap-2">
          <button type="button" onClick={onClose} data-testid="project-dialog-cancel" className="flex-1 border border-white/15 text-gray-300 hover:border-[#a68966] py-3 text-sm transition-colors">Annuler</button>
          <button type="button" data-testid="project-dialog-confirm" disabled={!del && !name.trim()}
            onClick={() => (del ? onDelete(action.pr) : onRename(action.pr, name.trim()))}
            className={`flex-1 font-semibold py-3 text-sm transition-colors disabled:opacity-50 ${del ? 'bg-red-900/80 hover:bg-red-800 text-white' : 'bg-[#a68966] hover:bg-[#b89c78] text-[#1a1a1a]'}`}>
            {del ? 'Supprimer' : 'Enregistrer'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientSpace() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [requests, setRequests] = useState([]);

  useEffect(() => { if (!loading && !user) navigate('/connexion', { replace: true }); }, [user, loading, navigate]);
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api('/me/projects'), api('/me/configurations')]);
    setProjects(a.ok ? await a.json() : []);
    setRequests(b.ok ? await b.json() : []);
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const [action, setAction] = useState(null);
  const [sharing, setSharing] = useState(null);
  const rename = async (pr, name) => {
    setAction(null);
    const r = await api(`/me/projects/${pr.id}`, { method: 'PUT', body: JSON.stringify({ name, config: pr.config }) });
    if (r.ok) { const u = await r.json(); setProjects((l) => l.map((x) => (x.id === u.id ? u : x))); toast.success('Projet renommé'); }
  };
  const remove = async (pr) => {
    setAction(null);
    const r = await api(`/me/projects/${pr.id}`, { method: 'DELETE' });
    if (r.ok) { setProjects((l) => l.filter((x) => x.id !== pr.id)); toast.success('Projet supprimé'); }
  };
  const logout = async () => { await logoutUser(); navigate('/'); toast.info('À bientôt chez JENMA Concept'); };

  if (!user) return <div className="min-h-screen bg-[#121212] flex items-center justify-center"><Loader2 className="w-6 h-6 text-gold animate-spin" /></div>;
  return (
    <div className="min-h-screen bg-[#121212] text-gray-200 pt-44 pb-24" data-testid="client-space-page">
      <div className="container mx-auto px-6">
        <header className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div className="flex items-center gap-5">
            {user.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full ring-2 ring-gold/60 object-cover" /> : <span className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center font-playfair text-2xl text-gold">{user.name?.[0]}</span>}
            <div>
              <p className="text-[11px] uppercase tracking-[0.4em] text-gold mb-2">Espace client</p>
              <h1 className="font-playfair text-4xl sm:text-5xl text-white" data-testid="client-space-welcome">Bonjour, <em className="text-[#d9c3a0]">{user.name?.split(' ')[0]}</em></h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            {user.is_admin && <a href="/dashboard" data-testid="client-space-admin-link" className="flex items-center gap-2 border border-neon/40 text-neon hover:bg-neon/10 px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors"><LayoutDashboard className="w-4 h-4" />Dashboard admin</a>}
            <button type="button" onClick={logout} data-testid="client-logout-btn" className="flex items-center gap-2 border border-white/15 text-gray-400 hover:text-gold hover:border-gold px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors"><LogOut className="w-4 h-4" />Déconnexion</button>
          </div>
        </header>

        <section className="mb-16">
          <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
            <h2 className="font-playfair text-2xl text-white flex items-center gap-3 whitespace-nowrap"><FolderOpen className="w-5 h-5 text-gold" />Mes projets <span className="text-gold text-base">({projects?.length ?? 0})</span></h2>
            <a href="/configurateur" data-testid="client-new-project-btn" className="flex items-center gap-2 bg-gold hover:bg-gold-light text-dark font-semibold px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors"><Plus className="w-4 h-4" /><span className="whitespace-nowrap">Nouveau projet</span></a>
          </div>
          {!projects ? <Loader2 className="w-6 h-6 text-gold animate-spin" /> : projects.length === 0 ? (
            <a href="/configurateur" className="group flex flex-col items-center justify-center gap-3 border border-dashed border-white/15 hover:border-gold/60 py-16 text-center transition-colors" data-testid="client-empty-projects">
              <p className="font-playfair text-xl text-white">Aucun projet enregistré</p>
              <p className="text-sm text-gray-500">Composez votre cuisine, dressing ou salle de bain puis cliquez sur « Enregistrer ».</p>
              <span className="flex items-center gap-1 text-gold text-xs uppercase tracking-[0.2em] mt-2">Ouvrir le configurateur <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
            </a>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">{projects.map((pr, i) => <ProjectCard key={pr.id} pr={pr} i={i} onRename={(pr) => setAction({ type: 'rename', pr })} onDelete={(pr) => setAction({ type: 'delete', pr })} onShare={setSharing} />)}</div>
          )}
        </section>

        <section>
          <h2 className="font-playfair text-2xl text-white mb-6">Mes demandes envoyées</h2>
          <Requests items={requests} />
        </section>
        <ShareDialog open={!!sharing} onOpenChange={(v) => !v && setSharing(null)} cfg={sharing?.config || {}} label={sharing ? getCategory(sharing.category).label : ''} name={sharing?.name} />
        <ProjectDialog action={action} onClose={() => setAction(null)} onRename={rename} onDelete={remove} />
      </div>
    </div>
  );
}
